import { EventAggregator } from "aurelia-event-aggregator";
import { autoinject, computedFrom, transient, Disposable, observable } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { activationStrategy, Router } from "aurelia-router";
import { MobileHelper } from "../../../../../helpers/mobile-helper";
import { StepModel } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { RatingsSummaryModel } from "../../../../../models/vessels/vessel.models";
import { SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { ClipboardCopyService } from "../../../../../utilities/clipboard-copy-service";
import { Reflection } from "../../../../../utilities/reflection";
import { MenuItem } from "../../../../components/menu/menu-item";
import { VesselFbrPerformance } from "../../vessels/steps/vessel-fbr-performance";
import { VesselPortCallRiskScoreModel, type VesselPreArrivalDetailsModel, VesselPreArrivalSteps } from "../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../services/ports-and-terminals.service";
import { VesselPreArrivalSummary } from "./steps/vessel-pre-arrival-summary";
import { VesselLists } from "../../vessels/steps/vessel-lists";
import { Messages } from "../../../../../configuration/event-messages.constants";
import { VesselPreArrivalRiskHistory } from "./steps/vessel-pre-arrival-risk-history";
import * as Enumerable from "linq";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../constants/basics/rightship-dialog.constants";
import { Claims } from "../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { RightShipQiRouterConstants } from "../../../../../configuration/rightshipqi-router.constants";
import { VesselPreArrivalAssignment } from "./components/vessel-pre-arrival-assignment/vessel-pre-arrival-assignment";

@autoinject
@transient()
export class VesselPreArrival {

    private _subscriptions: Disposable[] = [];
    private _args: any;
    private _rightShipUser: boolean;

    public viewModelInCompose: any = null;
    public modelInCompose: any = null;
    public currentTab: StepModel = null;
    public currentTabKey: string = "vessel-pre-arrival-tabs";
    public isSanctioned: boolean;
    public tabs: StepModel[] = [];
    public vesselId: number;
    public ratingModel: RatingsSummaryModel;
    public showSafetyScore: boolean;

    public vetRiskScores: VesselPortCallRiskScoreModel[];
    public canChangeVesselPreArrivalStatus: boolean = true;
    public vesselPreArrivalAssignment: VesselPreArrivalAssignment;
    public menuItems: MenuItem[] = [];

    @observable()
    public vesselPreArrivalDetails: VesselPreArrivalDetailsModel;

    public determineActivationStrategy() {
        return activationStrategy.replace;
    }

    @computedFrom('menuItems', 'menuItems.length', 'vesselPreArrivalDetails', 'vesselPreArrivalDetails.vesselId')
    get isMenuVisible() {
        return this.vesselPreArrivalDetails && this.vesselPreArrivalDetails.vesselId && this.menuItems && this.menuItems.filter(x => typeof x.isVisible === "function" ? x.isVisible() : x.isVisible).length > 0;
    }

    @computedFrom("vesselPreArrivalDetails.imo", "vesselPreArrivalDetails.vin", "vesselPreArrivalDetails.eni")
    public get vesselIdentifier(): string {
        if (this.vesselPreArrivalDetails.imo) {
            return ` (${this.vesselPreArrivalDetails.imo})`;
        }
        else if (this.vesselPreArrivalDetails.vin) {
            return ` (${this.vesselPreArrivalDetails.vin})`;
        }
        else if (this.vesselPreArrivalDetails.eni) {
            return ` (${this.vesselPreArrivalDetails.eni})`;
        }
        return '';
    }

    @computedFrom(
        'vesselPreArrivalDetails',
        'vesselPreArrivalDetails.estimatedArrivalTime',
        'vesselPreArrivalDetails.estimatedDepartureTime'
    )
    public get title(): string {

        if (new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) > new Date()) {
            return "PRE-ARRIVALS";
        } else if (new Date(this.vesselPreArrivalDetails.estimatedArrivalTime) < new Date() &&
            new Date(this.vesselPreArrivalDetails.estimatedDepartureTime) > new Date()) {
            return "IN PORT";
        }
        else {
            return "DEPARTED";
        }
    }

    @computedFrom('_args')
    public get vesselPortCallId(): number {
        return this._args ? Number(this._args.id) : null;
    }

    @computedFrom(Reflection.getPropertyPath<VesselPreArrival>(v => v.router.currentInstruction))
    public get previousInstructionTitle(): string {
        let title = this.router.history.getState("previousRouteName");

        if (title && title.trim() != "" && title != this.i18N.tr('back-text'))
            return title;

        const prevInstruction = this.router.currentInstruction.previousInstruction;
        title = prevInstruction
            ? prevInstruction.config.title
            : this.i18N.tr('back-text');

        this.router.history.setState("previousRouteName", title);

        return title;
    }

    @computedFrom("rightShipUser", "isSanctioned")
    public get shouldLimitSanctionedVesselAccess(): boolean {
        return !this._rightShipUser && this.isSanctioned;
    }

    @computedFrom("vesselPreArrivalDetails", "vesselPreArrivalDetails.statuses")
    public get statusHeaderClass(): string {
        const lastStatus = Enumerable.from(this.vesselPreArrivalDetails.statuses).orderByDescending(x => new Date(x.date)).firstOrDefault();
        if (lastStatus && lastStatus.name) {
            return lastStatus.name == 'Reviewed (with warnings)' ? 'reviewed-with-warnings' : lastStatus.name.split(' ').join('-').toLowerCase();
        }
    }

    @computedFrom("vesselPreArrivalDetails", "vesselPreArrivalDetails.statuses")
    public get status(): string {
        const lastStatus = Enumerable.from(this.vesselPreArrivalDetails.statuses).orderByDescending(x => new Date(x.date)).firstOrDefault();
        return lastStatus ? lastStatus.name : null;
    }

    constructor(private router: Router,
        private portsAndTerminalsService: PortsAndTerminalsService,
        private userClientService: UserClientService,
        private dialogClientService: DialogClientService,
        private clipboardCopyService: ClipboardCopyService,
        private signalClientService: SignalClientService,
        private vesselPreArrivalSummary: VesselPreArrivalSummary,
        private vesselPreArrivalRiskHistory: VesselPreArrivalRiskHistory,
        private vesselFbrPerformance: VesselFbrPerformance,
        private i18N: I18N,
        private eventAggregator: EventAggregator,
        private vesselLists: VesselLists,
        private userClaimsService: UserClaimsService,
        private googleAnalyticsService: GoogleAnalyticsService) {
    }

    private setTabs() {
        this.tabs = [
            {
                lazyLoad: true,
                show: () => true,
                title: "Summary",
                step: VesselPreArrivalSteps.Summary,
                onClick: () => {
                    this.resetTabState();
                    this.viewModelInCompose = this.vesselPreArrivalSummary;
                    this.modelInCompose = {
                        vesselPreArrivalDetails: this.vesselPreArrivalDetails,
                        vetRiskScores: this.vetRiskScores,
                        refreshVesselPreArrival: this.refreshVesselPreArrival
                    }
                }
            },
            {
                lazyLoad: true,
                show: () => true,
                title: "Risk history",
                step: VesselPreArrivalSteps.RiskHistory,
                onClick: () => {
                    this.resetTabState();
                    this.viewModelInCompose = this.vesselPreArrivalRiskHistory;
                    this.modelInCompose = { vesselPreArrivalDetails: this.vesselPreArrivalDetails }
                }
            },
            {
                lazyLoad: true,
                show: () => true,
                title: "FBR Performance",
                step: VesselPreArrivalSteps.Fbr,
                onClick: () => {
                    this.resetTabState();
                    this.viewModelInCompose = this.vesselFbrPerformance;
                    this.modelInCompose = {
                        vesselId: this.vesselId
                    }
                }
            },
            {
                lazyLoad: true,
                title: "Lists (" + (this.vesselPreArrivalDetails ? this.vesselPreArrivalDetails.listCount : 0) + ")",
                show: () => true,
                step: VesselPreArrivalSteps.Lists,
                onClick: () => {
                    if (this.shouldLimitSanctionedVesselAccess) return false;
                    this.resetTabState();
                    this.viewModelInCompose = this.vesselLists;
                    this.modelInCompose = { vesselId: this.vesselId };
                },
                class: this.shouldLimitSanctionedVesselAccess ? "disabled" : ""
            },
        ]
    }

    copy(str: string) {
        this.clipboardCopyService.copy(str);
    }

    attached() {
        if (this._subscriptions) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }

        this.signalClientService.subscribe(SignalFunctions.refreshAutoScreening, this.refreshVesselPreArrival);
        this.signalClientService.subscribe(SignalFunctions.systemRuleChanged, this.refreshVesselPreArrival);
        this.signalClientService.subscribe(SignalFunctions.vesselPreArrivalStatusChanged, this.refreshVesselPreArrivalStatus);
        this.signalClientService.subscribe(SignalFunctions.vesselsSynced, this.refreshVesselPreArrival);

        this._subscriptions.push(this.eventAggregator.subscribe(Messages.changeEntityItemStep, (model: { step: number }) => {
            this.currentTab = this.tabs.filter(x => x.step == model.step)[0];
            this.currentTab.onClick(null);
        }));
    }

    detached() {

        if (this._subscriptions) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }

        this.signalClientService.unsubscribe(SignalFunctions.refreshAutoScreening, this.refreshVesselPreArrival);
        this.signalClientService.unsubscribe(SignalFunctions.systemRuleChanged, this.refreshVesselPreArrival);
        this.signalClientService.unsubscribe(SignalFunctions.vesselPreArrivalStatusChanged, this.refreshVesselPreArrivalStatus);
        this.signalClientService.unsubscribe(SignalFunctions.vesselsSynced, this.refreshVesselPreArrival);
    }

    refreshVesselPreArrivalStatus = (params) => {
        if (params.vesselPortCallId == this.vesselPortCallId) {

            this.portsAndTerminalsService.getVesselPortCallStatuses(this.vesselPortCallId).then(statuses => {
                this.vesselPreArrivalDetails.statuses = statuses;
            });
        }
    }

    refreshVesselPreArrival = (params: { vetId: number, vesselIds: number[], vesselPortCallId: number }) => {
        if (params && ((params.vesselIds && params.vesselIds.indexOf(this.vesselId) > -1) ||
            (params.vesselPortCallId == this.vesselPortCallId) ||
            (params.vetId == this.vesselPreArrivalDetails.vetId))) {
            this.portsAndTerminalsService.getVesselPreArrival({
                vesselPortCallId: Number(this._args.id)
            }).then(response => {
                this.vesselPreArrivalDetails = response.vesselPreArrivalDetails;
                this.vetRiskScores = response.vetRiskScores;

                //force click to refresh tab content
                if (this.currentTab) {
                    this.currentTab.onClick(null);
                }
            }).catch(e => {
                console.error("Failed To refresh vesselDetails. error: " + e.message);
            });
        }
    }

    scrollToTop() {
        const scrollElement = MobileHelper.getScrollParent(document.getElementsByClassName('page-vessel').item(0), false) as HTMLDivElement;

        if (scrollElement) {
            scrollElement.scrollTop = 0;
        }
    }

    async activate(args: any) {
        if (!args?.id) return;

        this._args = args;
        try {

            const response = await this.portsAndTerminalsService.getVesselPreArrival({
                vesselPortCallId: Number(args.id)
            });

            this.vesselId = response.vesselPreArrivalDetails.vesselId;
            this.vesselPreArrivalDetails = response.vesselPreArrivalDetails;
            this.vetRiskScores = response.vetRiskScores;

            //#region Route Subtitle

            const imo = this.vesselPreArrivalDetails.imo
                ? this.vesselPreArrivalDetails.imo
                : this.vesselPreArrivalDetails.vin
                    ? this.vesselPreArrivalDetails.vin
                    : this.vesselPreArrivalDetails.eni
                        ? this.vesselPreArrivalDetails.eni
                        : 'N/A';

            const label = this.vesselPreArrivalDetails.imo
                ? 'IMO'
                : this.vesselPreArrivalDetails.eni
                    ? 'ENI'
                    : this.vesselPreArrivalDetails.vin
                        ? 'VIN'
                        : 'IMO';

            this.googleAnalyticsService.sendCustomGA4Event('vessel_pre_arrival_view', `${this.vesselPreArrivalDetails.vesselName} ${label} ${imo}`);
            //#endregion

            this.setTabs();

            if (this.shouldLimitSanctionedVesselAccess) {
                //if user is not rs admin and vessel is sanctioned set tab to overview
                this.currentTab = this.tabs[0];
                this.currentTab.onClick(null);
            }
            else {
                if (window.history?.state && Object.keys(window.history.state).indexOf("current-tab") > -1) {
                    const step = window.history.state["current-tab"];
                    const tabs = this.tabs.filter(x => x.step == step);

                    if (tabs?.length > 0) {
                        this.currentTab = tabs[0];
                        this.currentTab.onClick(null);
                    }
                }
            }

            setTimeout(() => {
                this.router.currentInstruction.config.title = this.vesselPreArrivalDetails.vesselName;
                this.router.currentInstruction.config.subTitle = `${label} ${imo}`;
            });

        } catch (e) {
            console.error(e);
            this.setTabs();
        }
    }

    public openVesselPreArrivalStatusDialog = () => {
        if (this.canChangeVesselPreArrivalStatus) {
            const lastStatus = Enumerable.from(this.vesselPreArrivalDetails.statuses).orderByDescending(x => new Date(x.date)).firstOrDefault();
            if (lastStatus && lastStatus.userId == this.userClientService.currentUser.id) {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VesselPreArrivalStatusDialog,
                    {
                        vesselPortCallId: this.vesselPortCallId,
                        statusId: lastStatus.statusId,
                        reload: (() => {
                            this.refreshVesselPreArrival({ vetId: this.vesselPreArrivalDetails.vetId, vesselIds: [this.vesselPreArrivalDetails.vesselId], vesselPortCallId: this.vesselPortCallId });
                        }),
                        vesselName: this.vesselPreArrivalDetails.vesselName,
                        internationalMaritimeOrganizationNumber: this.vesselPreArrivalDetails.imo,
                        europeanNumberOfIdentification: this.vesselPreArrivalDetails.eni,
                        vehicleIdentificationNumber: this.vesselPreArrivalDetails.imo
                    }, true, false, false, 'right');
            } else {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VesselPreArrivalAssignmentDialog, {
                    name: "Vessel Pre Arrival",
                    submit: () => this.vesselPreArrivalAssignment.assignToMe(null),
                    callback: () => this.openVesselPreArrivalStatusDialog(),
                    currentAssignment: lastStatus.userId ? lastStatus.user : null
                }, false, false, false, "right");
            }
        }
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    private resetTabState() {
        if (window.history && window.history.state && Object.keys(window.history.state).indexOf("current-tab") > -1) {
            delete window.history.state["current-tab"];
            window.history.replaceState(window.history.state, "Vessel");
        }
    }

    addFbr() {
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.NewFbr, {
            vesselId: this.vesselPreArrivalDetails.vesselId,
            cargoTypeCategoryId: this.vesselPreArrivalDetails.cargoTypeCategoryId
        });
    }

    addToList() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddToList,
            {
                entityId: this.vesselPreArrivalDetails.vesselId,
                entityType: EntityTypes.Vessel
            }, false, false, false, 'right');
    }

    openRequestFormsDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PortAndTerminalsRequestFormsDialog,
            {
                portSKey: this.vesselPreArrivalDetails.portSKey,
                vesselBKey: this.vesselPreArrivalDetails.vesselSKey,
                vesselName: this.vesselPreArrivalDetails.vesselName,
                vesselImoNumber: this.vesselPreArrivalDetails.identifier,
                vesselPortCallId: this.vesselPreArrivalDetails.vesselPortCallId
            }, false, false, false, "right");
    }

    public vesselPreArrivalDetailsChanged() {
        if (this.vesselPreArrivalDetails) {
            this.menuItems = [
                {
                    click: () => this.addFbr(),
                    isVisible: this.userClaimsService.hasPermission(Claims.CanAddFBR) && !this.isSanctioned,
                    item: {
                        addicon: true,
                        text: 'Create feedback report'
                    },
                    classList: '',
                },
                {
                    isVisible: () => this.userClaimsService.hasPermission(Claims.CanAddEntityToLists),
                    item: {
                        addicon: true,
                        text: 'Add to List'
                    },
                    classList: '',
                    click: () => this.addToList(),
                },
                {
                    isVisible: () => true,
                    item: {
                        addicon: true,
                        text: 'Request forms'
                    },
                    classList: '',
                    click: () => this.openRequestFormsDialog(),
                }
            ].filter(x => x.isVisible);
        } else {
            this.menuItems = [];
        }
    }
}
